'use client';

import React, { useState } from 'react';
import Head from 'next/head';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';

const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || 'http://localhost:3000';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus('loading');
    setErrorMsg('');

    try {
      const supabase = createClient();
      const { error } = await supabase
        .from('contact_messages_admin')
        .insert({
          name: form.name.trim(),
          email: form.email.toLowerCase().trim(),
          subject: form.subject.trim() || null,
          message: form.message.trim(),
        });

      if (error) {
        setErrorMsg('Failed to send message. Please try again.');
        setStatus('error');
      } else {
        setStatus('success');
        setForm({ name: '', email: '', subject: '', message: '' });
      }
    } catch {
      setErrorMsg('Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background)', color: 'var(--foreground)' }}>
      <Head>
        <title>Contact True Hauntings Podcast</title>
        <meta name="description" content="Get in touch with Anne and Renata. Share ghost stories, suggest haunted locations, or inquire about collaborations with True Hauntings." />
        <link rel="canonical" href={`${baseUrl}/contact`} />
        <meta property="og:title" content="Contact True Hauntings" />
        <meta property="og:description" content="Share your paranormal stories or collaborate with True Hauntings podcast." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${baseUrl}/contact`} />
      </Head>
      <Header />

      <main className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="mb-8 sm:mb-10">
            <p className="text-xs font-mono tracking-widest uppercase mb-2" style={{ color: 'var(--primary)' }}>Get in Touch</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" style={{ fontFamily: 'var(--font-display)' }}>
              Contact Us
            </h1>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
              Have a ghost story to share? A haunted location we should investigate? Or just want to say hello? We'd love to hear from you.
            </p>
          </div>

          {/* Fan mail */}
          <div className="mt-8 glass-card p-6 rounded-lg text-center">
            <h2 className="text-lg sm:text-xl font-bold mb-2" style={{ fontFamily: 'var(--font-display)' }}>
              We Love Fan Mail. Send Us Some Goodies!
            </h2>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
              Anne &amp; Renata, PO Box 2285, Dangar NSW 2309 Australia
            </p>
          </div>

          {status === 'success' ? (
            <div className="mt-8 glass-card p-8 sm:p-10 rounded-lg text-center">
              <div className="text-4xl mb-4">👻</div>
              <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: 'var(--font-display)' }}>
                Message Received!
              </h2>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                Anne and Renata will get back to you soon. In the meantime, why not listen to an episode?
              </p>
              <button
                onClick={() => setStatus('idle')}
                className="btn-ghost mt-6 px-6 py-3 text-xs"
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <div className="mt-8 glass-card p-6 sm:p-8 rounded-lg">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-mono tracking-widest uppercase mb-2" style={{ color: 'var(--muted-foreground)' }}>
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                      className="w-full px-4 py-3 text-sm outline-none rounded"
                      style={{
                        background: 'rgba(30,16,64,0.6)',
                        border: '1px solid var(--border)',
                        color: 'var(--foreground)',
                        fontFamily: 'var(--font-mono)',
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-mono tracking-widest uppercase mb-2" style={{ color: 'var(--muted-foreground)' }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 text-sm outline-none rounded"
                      style={{
                        background: 'rgba(30,16,64,0.6)',
                        border: '1px solid var(--border)',
                        color: 'var(--foreground)',
                        fontFamily: 'var(--font-mono)',
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase mb-2" style={{ color: 'var(--muted-foreground)' }}>
                    Subject
                  </label>
                  <select
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-sm outline-none rounded"
                    style={{
                      background: 'rgba(30,16,64,0.6)',
                      border: '1px solid var(--border)',
                      color: form.subject ? 'var(--foreground)' : 'var(--muted-foreground)',
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    <option value="">Select a subject...</option>
                    <option value="Ghost Story Submission">Ghost Story Submission</option>
                    <option value="Location Suggestion">Location Suggestion</option>
                    <option value="Media Inquiry">Media Inquiry</option>
                    <option value="Collaboration">Collaboration</option>
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase mb-2" style={{ color: 'var(--muted-foreground)' }}>
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    placeholder="Tell us your story, ask a question, or just say hello..."
                    className="w-full px-4 py-3 text-sm outline-none rounded resize-none"
                    style={{
                      background: 'rgba(30,16,64,0.6)',
                      border: '1px solid var(--border)',
                      color: 'var(--foreground)',
                      fontFamily: 'var(--font-mono)',
                    }}
                  />
                </div>

                {errorMsg && (
                  <p className="text-xs" style={{ color: '#ef4444' }}>{errorMsg}</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="btn-primary w-full py-4 text-xs"
                >
                  {status === 'loading' ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                      </svg>
                      Sending...
                    </span>
                  ) : 'Send Message'}
                </button>
              </form>
            </div>
          )}

          {/* Alternative contact */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { label: 'Instagram', href: 'https://www.instagram.com/anneandrenata/', icon: '📸' },
              { label: 'Facebook', href: 'https://www.facebook.com/TrueHauntingsPodcast/', icon: '👥' },
            ].map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="glass-card p-4 rounded-lg flex items-center gap-3 transition-colors group"
              >
                <span className="text-xl">{social.icon}</span>
                <div>
                  <p className="text-xs font-mono tracking-widest uppercase" style={{ color: 'var(--muted-foreground)' }}>
                    Follow on
                  </p>
                  <p className="text-sm font-semibold group-hover:text-primary transition-colors" style={{ fontFamily: 'var(--font-display)' }}>
                    {social.label}
                  </p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
